import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { PostgrestError } from '@supabase/supabase-js';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { debugLog, debugError, debugWarn } from '@/lib/debugUtils';
import { useAuth } from '@/contexts/AuthContext';

interface SimpleKeywordQuery {
  query: string;
  language: string;
  location: string;
  dataType: string;
  userId: string;
  workspaceId?: string; // Added workspace filtering
  strictWorkspaceFiltering?: boolean; // Controls workspace filtering behavior
}

interface KeywordResult {
  keyword: string;
  data_type: string; // Added to match database schema
  search_volume?: number;
  intent?: string;
  competition?: string;
  difficulty?: number;
  cpc?: number;
  funnel_stage?: string;
  url?: string;
  title?: string;
  description?: string;
  domain?: string;
  subtopic?: string;
  primary_keyword?: string;
  people_also_ask?: string;
  answer?: string;
  [key: string]: unknown; // Add index signature for table compatibility
}

interface UseSimpleKeywordQueryReturn {
  data: KeywordResult[] | null;
  isLoading: boolean;
  error: PostgrestError | null;
  executeQuery: (params: SimpleKeywordQuery) => Promise<void>;
  clearResults: () => void;
  startRealtime: (params: SimpleKeywordQuery) => void;
  stopRealtime: () => void;
  connectionStatus: 'idle' | 'connected' | 'disconnected';
}

/**
 * Validates if a user ID is a valid Supabase UUID
 * @param userId - The user ID to validate
 * @returns true if valid UUID, false otherwise
 */
const isValidSupabaseUserId = (userId: string): boolean => {
  // Check if it's a valid UUID format (8-4-4-4-12 hexadecimal characters)
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(userId);
};

/**
 * Checks if a user ID looks like a Clerk user ID
 * @param userId - The user ID to check
 * @returns true if it looks like a Clerk ID
 */
const isClerkUserId = (userId: string): boolean => {
  return userId.startsWith('user_');
};

/**
 * Validates workspace ID format
 * @param workspaceId - The workspace ID to validate
 * @returns true if valid UUID format
 */
const isValidWorkspaceId = (workspaceId: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(workspaceId);
};

/**
 * Real-time keyword query service with WebSocket subscriptions
 * - Executes query immediately when called (no delays)
 * - Real-time updates via Supabase WebSocket subscriptions
 * - Instant data updates when new keyword data is available
 * - Better error handling and logging
 * - Uses the original Supabase client for authentication
 * - Strict user_id filtering: No user_id = No data access
 * - Enhanced user ID validation to prevent Clerk IDs from reaching Supabase
 * - WORKSPACE FILTERING: Ensures data isolation between workspaces
 * - Real-time notifications when keyword research completes
 */
export const useSimpleKeywordQuery = (): UseSimpleKeywordQueryReturn => {
  const { user } = useAuth();
  const userEmail = user?.primaryEmailAddress?.emailAddress;
  
  const [data, setData] = useState<KeywordResult[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<PostgrestError | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'connected' | 'disconnected'>('idle');
  
  // Real-time subscription management
  const channelRef = useRef<RealtimeChannel | null>(null);
  const currentParamsRef = useRef<SimpleKeywordQuery | null>(null);
  const mountedRef = useRef<boolean>(true);

  // Map frontend data types to database values
  const getDbDataType = (dataType: string): string => {
    const mapping: { [key: string]: string } = {
      'related': 'related_keywords',
      'ideas': 'keyword_ideas',
      'suggestions': 'keyword_suggestions',
      'subtopics': 'subtopics',
      'paa': 'people_also_ask',
      'trends': 'google_trends',
      'serp': 'serp_analysis'
    };
    return mapping[dataType] || dataType;
  };

  // Get appropriate columns for each data type
  const getSelectColumns = (dataType: string): string => {
    switch (dataType) {
      case 'serp':
        return 'url, title, description, domain, primary_keyword, location, language';
      case 'subtopics':
        return 'subtopic, primary_keyword, location, language';
      case 'paa':
        return 'people_also_ask, answer, primary_keyword';
      case 'trends':
        return 'primary_keyword, search_volume, date_from, date_to, location';
      default:
        return 'keyword, search_volume, intent, competition, difficulty, cpc, funnel_stage, location, language';
    }
  };

  const executeQuery = useCallback(async (params: SimpleKeywordQuery) => {
    const { query, language, location, dataType, userId, workspaceId, strictWorkspaceFiltering = false } = params;

    // Enhanced validation with specific error messages
    if (!userId) {
      setError({
        name: 'AuthenticationError',
        message: 'User must be authenticated to query keyword data',
        details: 'Authentication required',
        hint: 'Please sign in to continue',
        code: 'AUTH_REQUIRED',
      } as PostgrestError);
      return;
    }

    // Validate that we have a proper Supabase UUID, not a Clerk user ID
    if (isClerkUserId(userId)) {
      debugError(userEmail, '[SimpleKeywordQuery] Clerk user ID detected:', userId);
      setError({
        name: 'InvalidUserIdError',
        message: 'Invalid user ID format: Clerk user IDs are not supported for database queries',
        details: `Received Clerk user ID: ${userId}. Expected Supabase UUID format.`,
        hint: 'Please ensure user ID mapping is working correctly in AuthContext',
        code: 'INVALID_USER_ID_FORMAT',
      } as PostgrestError);
      return;
    }

    if (!isValidSupabaseUserId(userId)) {
      debugError(userEmail, '[SimpleKeywordQuery] Invalid UUID format:', userId);
      setError({
        name: 'InvalidUserIdError',
        message: 'Invalid user ID format: Must be a valid UUID',
        details: `Received: ${userId}. Expected UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx`,
        hint: 'Please ensure user authentication and ID mapping is working correctly',
        code: 'INVALID_UUID_FORMAT',
      } as PostgrestError);
      return;
    }

    // Validate workspace ID if provided
    if (workspaceId && !isValidWorkspaceId(workspaceId)) {
      debugError(userEmail, '[SimpleKeywordQuery] Invalid workspace ID format:', workspaceId);
      setError({
        name: 'InvalidWorkspaceIdError',
        message: 'Invalid workspace ID format: Must be a valid UUID',
        details: `Received: ${workspaceId}. Expected UUID format.`,
        hint: 'Please ensure workspace ID is properly generated',
        code: 'INVALID_WORKSPACE_ID_FORMAT',
      } as PostgrestError);
      return;
    }

    if (!query?.trim()) {
      setData([]);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      debugLog(userEmail, `[SimpleKeywordQuery] Executing query with original client:`, {
        table: 'keyword_consolidated_data',
        dataType: getDbDataType(dataType),
        primaryKeyword: query,
        userId,
        workspaceId, // Added workspace context to logging
        strictWorkspaceFiltering,
        language,
        location
      });

      // DEBUGGING: Log query details for any keyword search
      console.log('🔍 [DEBUG] KEYWORD QUERY DETAILS:', {
        exact_query: query,
        exact_userId: userId,
        exact_dataType: getDbDataType(dataType),
        exact_language: language,
        exact_location: location,
        exact_workspaceId: workspaceId,
        exact_strictFiltering: strictWorkspaceFiltering
      });

      // Handle the consolidated 'keywords' tab that combines multiple data types
      if (dataType === 'keywords') {
        const keywordDataTypes = ['related_keywords', 'keyword_ideas', 'keyword_suggestions'];
        const allResults: KeywordResult[] = [];
        
        console.log('🔍 [DEBUG] KEYWORDS TAB: Querying multiple data types:', keywordDataTypes);
        
        // Query each data type and combine results
        for (const dbDataType of keywordDataTypes) {
          let supabaseQuery = supabase
            .from('keyword_consolidated_data')
            .select('keyword, search_volume, intent, competition, difficulty, cpc, funnel_stage, location, language')
            .eq('primary_keyword', query)
            .eq('user_id', userId)
            .eq('data_type', dbDataType)
            .not('keyword', 'is', null); // Exclude null keywords
            
          // Apply workspace filtering
          if (strictWorkspaceFiltering && workspaceId) {
            supabaseQuery = supabaseQuery.or(`workspace_id.eq.${workspaceId},workspace_id.is.null`);
          }
          
          // Apply language and location filters
          if (language) supabaseQuery = supabaseQuery.eq('language', language);
          if (location) supabaseQuery = supabaseQuery.eq('location', location);
          
          // Order and limit
          supabaseQuery = supabaseQuery.order('search_volume', { ascending: false }).limit(50);
          
          const { data: typeResults, error: typeError } = await supabaseQuery;
          
          if (typeError) {
            console.warn(`🔍 [DEBUG] Error querying ${dbDataType}:`, typeError);
          } else if (typeResults) {
            allResults.push(...(typeResults as unknown as KeywordResult[]));
            console.log(`🔍 [DEBUG] ${dbDataType}: ${typeResults.length} results`);
          }
        }
        
        // Sort combined results by search volume
        allResults.sort((a, b) => (b.search_volume || 0) - (a.search_volume || 0));
        
        console.log('🔍 [DEBUG] KEYWORDS TAB TOTAL RESULTS:', allResults.length);
        setData(allResults.slice(0, 100)); // Limit to 100 total results
        return;
      }
      
      // Build the regular single data type query
      let supabaseQuery = supabase
        .from('keyword_consolidated_data')
        .select(getSelectColumns(dataType))
        .eq('primary_keyword', query)
        .eq('user_id', userId) // STRICT: Always require valid user_id
        .eq('data_type', getDbDataType(dataType));

      // FLEXIBLE: Add workspace filtering based on strictWorkspaceFiltering flag
      if (strictWorkspaceFiltering && workspaceId) {
        // Strict mode: Only show data for this specific workspace or unassigned data
        supabaseQuery = supabaseQuery.or(`workspace_id.eq.${workspaceId},workspace_id.is.null`);
        debugLog(userEmail, `[SimpleKeywordQuery] Applied strict workspace filter: ${workspaceId} (includes unassigned data)`);
      } else if (workspaceId) {
        // Flexible mode: Show all user data, but log workspace context
        debugLog(userEmail, `[SimpleKeywordQuery] Flexible mode: showing all user data for keyword research (workspace context: ${workspaceId})`);
      } else {
        debugWarn(userEmail, `[SimpleKeywordQuery] No workspace ID provided - showing all user data`);
      }

      // Add language and location filters if provided
      if (language) {
        supabaseQuery = supabaseQuery.eq('language', language);
      }
      if (location) {
        supabaseQuery = supabaseQuery.eq('location', location);
      }

      // CRITICAL FIX: Exclude records with null keywords for keyword-based data types
      if (['related', 'ideas', 'suggestions'].includes(dataType)) {
        supabaseQuery = supabaseQuery.not('keyword', 'is', null);
      }

      // Order by search volume (most data types have this)
      if (['related', 'ideas', 'suggestions', 'trends'].includes(dataType)) {
        supabaseQuery = supabaseQuery.order('search_volume', { ascending: false });
      } else {
        supabaseQuery = supabaseQuery.order('created_at', { ascending: false });
      }

      // Reasonable limit
      supabaseQuery = supabaseQuery.limit(100);

      // DEBUGGING: Log the final query details for any keyword
      console.log('🔍 [DEBUG] FINAL QUERY ABOUT TO EXECUTE:', {
        base_table: 'keyword_consolidated_data',
        keyword: query,
        filters: {
          primary_keyword: query,
          user_id: userId,
          data_type: getDbDataType(dataType),
          language: language || 'NOT_SET',
          location: location || 'NOT_SET',
          workspace_filter: strictWorkspaceFiltering && workspaceId ? `${workspaceId} OR NULL` : 'NOT_APPLIED'
        }
      });

      const { data: resultData, error: supabaseError } = await supabaseQuery;

      if (supabaseError) {
        debugError(userEmail, `[SimpleKeywordQuery] Database error:`, supabaseError);
        throw supabaseError;
      }
      
      debugLog(userEmail, `[SimpleKeywordQuery] Query successful:`, resultData?.length || 0, 'results');
      
      // DEBUGGING: Log results for any keyword search
      console.log('🔍 [DEBUG] KEYWORD SEARCH RESULTS:', {
        keyword: query,
        total_results: resultData?.length || 0,
        first_result: resultData?.[0] || 'NO_RESULTS',
        sample_results: resultData?.slice(0, 3) || [] // Show first 3 results instead of all
      });
      
      const newData = (resultData || []) as unknown as KeywordResult[];
      setData(newData);

    } catch (e: any) {
      debugError(userEmail, `[SimpleKeywordQuery] Query failed:`, e);
      
      if (e instanceof Error && 'code' in e) {
        setError(e as PostgrestError);
      } else {
        const customError: PostgrestError = {
          name: 'QueryError',
          message: String(e?.message || 'Failed to query keyword data'),
          details: String(e?.details || ''),
          hint: String(e?.hint || ''),
          code: String(e?.code || 'QUERY_FAILED'),
        };
        setError(customError);
      }
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [userEmail]);

  /**
   * Set up real-time subscription for keyword data
   */
  const setupRealtimeSubscription = useCallback((params: SimpleKeywordQuery) => {
    if (!params.workspaceId || !params.userId) {
      debugWarn(userEmail, '[SimpleKeywordQuery] Missing workspaceId or userId for real-time subscription');
      return;
    }

    debugLog(userEmail, '[SimpleKeywordQuery] Setting up real-time subscription for:', params);

    const channel = supabase
      .channel(`keyword_research_${params.workspaceId}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen for INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'keyword_consolidated_data',
          filter: `workspace_id=eq.${params.workspaceId}`
        },
        (payload) => {
          debugLog(userEmail, '[SimpleKeywordQuery] Real-time update received:', payload.eventType);
          
          // Verify the change is for this user and matches our search parameters
          if (payload.new && 
              (payload.new as any).user_id === params.userId &&
              (payload.new as any).primary_keyword === params.query &&
              (payload.new as any).data_type === getDbDataType(params.dataType)) {
            debugLog(userEmail, '[SimpleKeywordQuery] Relevant update detected, refreshing data');
            executeQuery(params);
          }
        }
      )
      .subscribe((status) => {
        debugLog(userEmail, '[SimpleKeywordQuery] Subscription status:', status);
        
        if (status === 'SUBSCRIBED') {
          setConnectionStatus('connected');
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          setConnectionStatus('disconnected');
        }
      });

    channelRef.current = channel;
  }, [userEmail, executeQuery, getDbDataType]);

  /**
   * Clean up real-time subscription
   */
  const cleanupSubscription = useCallback(() => {
    debugLog(userEmail, '[SimpleKeywordQuery] Cleaning up real-time subscription');
    
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    
    setConnectionStatus('idle');
  }, [userEmail]);

  /**
   * Start real-time monitoring for keyword data
   */
  const startRealtime = useCallback((params: SimpleKeywordQuery) => {
    debugLog(userEmail, '[SimpleKeywordQuery] Starting real-time monitoring for:', params);
    
    // Clean up any existing subscription
    cleanupSubscription();
    
    // Store current params
    currentParamsRef.current = params;
    
    // Execute query immediately to get existing data
    executeQuery(params);
    
    // Set up real-time subscription for future updates
    setupRealtimeSubscription(params);
    
  }, [executeQuery, setupRealtimeSubscription, cleanupSubscription, userEmail]);

  /**
   * Stop real-time monitoring
   */
  const stopRealtime = useCallback(() => {
    debugLog(userEmail, '[SimpleKeywordQuery] Stopping real-time monitoring');
    
    cleanupSubscription();
    currentParamsRef.current = null;
  }, [cleanupSubscription, userEmail]);

  const clearResults = useCallback(() => {
    setData(null);
    setError(null);
    stopRealtime();
  }, [stopRealtime]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      cleanupSubscription();
    };
  }, [cleanupSubscription]);

  return {
    data,
    isLoading,
    error,
    executeQuery,
    clearResults,
    startRealtime,
    stopRealtime,
    connectionStatus,
  };
};